/**
 * BulkSendDialog — v1.9.0
 *
 * Composer for sending the same WhatsApp message to N leads at once.
 * Triggered from the LeadsListPage multi-select action bar.
 *
 * UX rules :
 *   - Show the count of recipients clearly, and warn if any are about
 *     to be skipped (no phone).
 *   - Support {nom} / {prenom} placeholders — same semantics as the
 *     welcome template.
 *   - During send: disable everything, show a spinner, refuse close.
 *   - On success: show the result breakdown (sent / deferred / failed)
 *     instead of a generic "OK" — the user often wants to know how many
 *     went out vs got queued for the morning.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Send,
  X,
  Users,
  AlertCircle,
  Clock,
  Loader2,
  Check,
  Paperclip,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { whatsappApi, documentsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";

export default function BulkSendDialog({
  open,
  onOpenChange,
  leads,
  totalSelected,
  onSent,
}) {
  const settings = useSettings() || {};
  // v2.0.0 — Cloud-aware. When the active project has Cloud enabled,
  // we offer a "mode template" toggle so the bulk send routes via the
  // official Meta API instead of Baileys. Templates avoid suspension
  // and are required for sends outside the 24h window.
  const activeProject = settings.activeProject;
  const cloudReady = !!(
    activeProject?.cloud_enabled &&
    activeProject?.cloud_phone_id &&
    activeProject?.cloud_access_token
  );

  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  // After a successful send we keep the result on screen until the user
  // dismisses the dialog — they may want to read the breakdown.
  const [result, setResult] = useState(null);

  // v2.0.0 — template mode. Loaded lazily on open if Cloud is ready.
  // templateName === "" means "free-form mode" (Baileys via routing).
  const [useTemplate, setUseTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateLanguage, setTemplateLanguage] = useState("fr");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // v2.8.9 — optional attachments from the Documents library.
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);

  // Reset state every time we re-open the dialog so a previous send's
  // result doesn't bleed into a new session.
  useEffect(() => {
    if (open) {
      setText("");
      setResult(null);
      setUseTemplate(false);
      setTemplateName("");
      setTemplateLanguage("fr");
      setSelectedDocIds([]);
    }
  }, [open]);

  // Load the Documents library when the dialog opens — scoped to the active
  // project (plus global docs) so the user attaches the right project's files.
  useEffect(() => {
    if (!open) return;
    setDocsLoading(true);
    documentsApi
      .list({ projetId: activeProject?.id || null, includeGlobal: true })
      .then((list) => setDocs(Array.isArray(list) ? list : list?.items || []))
      .catch(() => setDocs([]))
      .finally(() => setDocsLoading(false));
  }, [open, activeProject?.id]);

  const toggleDoc = (id) =>
    setSelectedDocIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Load Meta templates when the user toggles "Mode template" on.
  // Cached for the lifetime of the dialog session.
  useEffect(() => {
    if (!open || !useTemplate || !cloudReady || !activeProject?.id) return;
    if (templates.length > 0) return; // already loaded
    setTemplatesLoading(true);
    whatsappApi.cloud
      .templates(activeProject.id)
      .then((r) => {
        // Keep only APPROVED templates — PENDING / REJECTED can't be sent
        const approved = (r.templates || []).filter(
          (t) => (t.status || "").toUpperCase() === "APPROVED"
        );
        setTemplates(approved);
        if (approved.length === 0) {
          toast.info(
            "Aucun template approuvé sur ce projet. Soumets-en dans Meta Business Manager."
          );
        }
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || e?.message || "erreur";
        toast.error(`Templates Meta inaccessibles · ${detail}`);
      })
      .finally(() => setTemplatesLoading(false));
  }, [open, useTemplate, cloudReady, activeProject?.id, templates.length]);

  // Number of leads we'd silently skip because they have no phone. The
  // parent already filters by telephone (`selectedLeads`), so this is
  // simply the diff between the raw selection and the sendable subset.
  const sendable = leads?.length || 0;
  const skipped = Math.max(0, (totalSelected || 0) - sendable);

  const handleSend = async () => {
    const body = text.trim();
    // Template mode : body is optional (Meta renders from the template).
    // Attachment-only sends are allowed (a document with no text).
    if (!body && !useTemplate && selectedDocIds.length === 0) {
      toast.error("Ajoute un message ou une pièce jointe.");
      return;
    }
    if (useTemplate && !templateName) {
      toast.error("Sélectionne un template Meta.");
      return;
    }
    if (sendable === 0) {
      toast.error("Aucun destinataire avec un téléphone.");
      return;
    }
    setSending(true);
    try {
      const ids = leads.map((l) => l.id);
      const r = await whatsappApi.bulkSend({
        leadIds: ids,
        text: body || (useTemplate ? `[template:${templateName}]` : ""),
        documentIds: selectedDocIds,
        ...(useTemplate
          ? {
              templateName,
              templateLanguage,
              templateParamKeys: ["prenom"], // {{1}} = prenom by convention
            }
          : {}),
      });
      setResult(r);
      onSent?.(r);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || e?.message || "Échec de l'envoi groupé");
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    if (sending) return;
    onOpenChange(false);
  };

  // Result panel — shows after a successful POST. Color-coded so the user
  // can spot failures or deferred-quiet-hours at a glance.
  const renderResult = () => {
    if (!result) return null;
    const items = [
      { label: "Envoyés", value: result.sent, color: "text-emerald-700" },
      result.deferred > 0 && {
        label: "Différés (hors heures)",
        value: result.deferred,
        color: "text-amber-700",
      },
      result.failed > 0 && {
        label: "Échecs",
        value: result.failed,
        color: "text-red-700",
      },
      result.skipped_no_phone > 0 && {
        label: "Pas de téléphone",
        value: result.skipped_no_phone,
        color: "text-muted-foreground",
      },
      result.skipped_no_access > 0 && {
        label: "Refusés (accès)",
        value: result.skipped_no_access,
        color: "text-muted-foreground",
      },
    ].filter(Boolean);

    return (
      <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Check className="h-4 w-4 text-emerald-600" />
          Envoi groupé terminé · {result.total} destinataire(s)
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {items.map((it) => (
            <div
              key={it.label}
              className="flex items-center justify-between rounded bg-card px-2 py-1.5"
            >
              <span className="text-muted-foreground">{it.label}</span>
              <span className={`font-bold ${it.color}`}>{it.value}</span>
            </div>
          ))}
        </div>
        {result.quiet_window && (
          <p className="flex items-start gap-1.5 text-[11px] text-amber-700">
            <Clock className="mt-0.5 h-3 w-3 flex-none" />
            Hors heures d'envoi (08h-22h Dakar) — les messages sont programmés pour la prochaine fenêtre.
          </p>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (sending ? null : onOpenChange(o))}>
      <DialogContent
        className="sm:max-w-[560px] max-h-[88vh] overflow-y-auto"
        data-testid="bulk-send-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-600" />
            Envoi groupé WhatsApp
          </DialogTitle>
          <DialogDescription>
            Envoie le même message à <b>{sendable}</b> lead(s)
            {skipped > 0 && (
              <>
                {" "}
                — <span className="text-amber-700">{skipped} ignoré(s)</span>{" "}
                (pas de téléphone)
              </>
            )}
            .
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-3">
            {/* v2.0.0 — Mode template (Cloud API). Visible seulement si le
                projet actif a la Cloud API correctement configurée.
                Sinon : envoi free-form via Baileys (legacy). */}
            {cloudReady && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 dark:border-blue-900/40 dark:bg-blue-950/20">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useTemplate}
                    onChange={(e) => setUseTemplate(e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer accent-blue-600"
                    data-testid="bulk-template-toggle"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                      Envoyer comme template Meta (recommandé)
                    </p>
                    <p className="mt-0.5 text-[11px] text-blue-800 dark:text-blue-300">
                      Route via l'API officielle Meta — pas de risque de suspension,
                      fonctionne aussi hors fenêtre 24h.
                    </p>
                  </div>
                </label>

                {useTemplate && (
                  <div className="mt-3 space-y-2 border-t border-blue-200 pt-3 dark:border-blue-900/40">
                    <Label className="text-xs">Template approuvé</Label>
                    {templatesLoading ? (
                      <div className="rounded border border-border/60 bg-card px-3 py-2 text-xs text-muted-foreground">
                        Chargement depuis Meta…
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
                        Aucun template approuvé. Soumets-en un dans{" "}
                        <b>Meta Business Manager</b> et reviens ici une fois APPROVED.
                      </div>
                    ) : (
                      <select
                        value={templateName ? `${templateName}|${templateLanguage}` : ""}
                        onChange={(e) => {
                          const [name, lang] = e.target.value.split("|");
                          setTemplateName(name || "");
                          setTemplateLanguage(lang || "fr");
                        }}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        data-testid="bulk-template-select"
                      >
                        <option value="">— Choisir un template —</option>
                        {templates.map((t) => (
                          <option
                            key={`${t.name}|${t.language}`}
                            value={`${t.name}|${t.language}`}
                          >
                            {t.name} · {t.language} · {(t.category || "?").toLowerCase()}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      Le placeholder <code className="rounded bg-muted px-1">{"{{1}}"}</code>{" "}
                      du template sera remplacé par le <b>prénom</b> du lead.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">
                {useTemplate ? "Note interne (historique CRM)" : "Message"}
              </Label>
              <Textarea
                rows={useTemplate ? 3 : 6}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={
                  useTemplate
                    ? "Texte stocké dans l'historique CRM (le destinataire reçoit le template Meta)"
                    : "Bonjour {prenom}, j'ai du nouveau pour vous concernant le programme…"
                }
                disabled={sending}
                data-testid="bulk-send-text"
              />
              {!useTemplate && (
                <p className="text-[11px] text-muted-foreground">
                  Placeholders disponibles :{" "}
                  <code className="rounded bg-muted px-1">{"{nom}"}</code> et{" "}
                  <code className="rounded bg-muted px-1">{"{prenom}"}</code>.
                  Le compte commercial qui exécute envoie automatiquement
                  depuis le numéro WhatsApp du projet de chaque lead.
                </p>
              )}
            </div>

            {/* v2.8.9 — Pièces jointes depuis la bibliothèque Documents */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs">
                <Paperclip className="h-3.5 w-3.5" />
                Pièces jointes
                {selectedDocIds.length > 0 && (
                  <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                    {selectedDocIds.length} sélectionné{selectedDocIds.length > 1 ? "s" : ""}
                  </span>
                )}
              </Label>
              {docsLoading ? (
                <div className="rounded-md border border-border/60 bg-card px-3 py-2 text-xs text-muted-foreground">
                  Chargement des documents…
                </div>
              ) : docs.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  Aucun document dans la bibliothèque de ce projet. Ajoutes-en
                  dans <b>Paramètres → Documents</b>.
                </p>
              ) : (
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border/60 p-1.5">
                  {docs.map((d) => {
                    const checked = selectedDocIds.includes(d.id);
                    return (
                      <label
                        key={d.id}
                        className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
                          checked ? "bg-emerald-50 dark:bg-emerald-950/30" : "hover:bg-muted/50"
                        }`}
                        data-testid={`bulk-doc-${d.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDoc(d.id)}
                          disabled={sending}
                          className="h-4 w-4 cursor-pointer accent-emerald-600"
                        />
                        <FileText className="h-3.5 w-3.5 flex-none text-muted-foreground" />
                        <span className="truncate">{d.nom || d.filename}</span>
                        {!d.projet_id && (
                          <span className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">
                            global
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
              {selectedDocIds.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  {selectedDocIds.length} pièce{selectedDocIds.length > 1 ? "s" : ""} jointe{selectedDocIds.length > 1 ? "s" : ""} envoyée{selectedDocIds.length > 1 ? "s" : ""} avec le message, via le numéro du projet de chaque lead.
                </p>
              )}
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
              <span>
                Hors fenêtre 08h-22h Dakar, l'envoi est <b>programmé</b> pour le
                prochain matin (pas de drop). Quota max&nbsp;: 200/jour par numéro.
              </span>
            </div>
          </div>
        )}

        {renderResult()}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="ghost"
            onClick={close}
            disabled={sending}
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            {result ? "Fermer" : "Annuler"}
          </Button>
          {!result && (
            <Button
              onClick={handleSend}
              disabled={
                sending ||
                sendable === 0 ||
                (useTemplate
                  ? !templateName
                  : !text.trim() && selectedDocIds.length === 0)
              }
              className={`gap-1.5 ${
                useTemplate
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-emerald-600 hover:bg-emerald-700"
              } text-white`}
              data-testid="bulk-send-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Envoi en cours…
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5" />
                  Envoyer à {sendable} lead{sendable > 1 ? "s" : ""}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
